import { useEffect, useState, useCallback } from "react";
import { Plus, Upload, Pencil, Trash2, Users, Download, CopyPlus } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, downloadFile } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import { fmtEur, monthKey } from "@/lib/format";
import { EmployeeDialog } from "@/components/EmployeeDialog";
import { CsvImportDialog } from "@/components/CsvImportDialog";
import { DuplicateYearDialog } from "@/components/DuplicateYearDialog";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const annualOf = (e, year) => Array.from({ length: 12 }, (_, i) => Number(e.remunerations?.[monthKey(year, i + 1)] || 0)).reduce((a, b) => a + b, 0);

export default function EmployeesPage() {
  const { company, companyId, year, setYear } = useCompany();
  const [employees, setEmployees] = useState([]);
  const [summary, setSummary] = useState(null);
  const [dialog, setDialog] = useState({ open: false, employee: null });
  const [importOpen, setImportOpen] = useState(false);
  const [dupOpen, setDupOpen] = useState(false);

  const exportCsv = async () => {
    try {
      await downloadFile(`/companies/${companyId}/employees/export?annee=${year}`, `salaries-${year}.csv`);
      toast.success("Export CSV des salariés téléchargé");
    } catch (e) { toast.error(apiError(e)); }
  };

  const load = useCallback(async () => {
    if (!companyId) return;
    const [emps, sum] = await Promise.all([
      api.get(`/companies/${companyId}/employees`),
      api.get(`/companies/${companyId}/summary`, { params: { annee: year } }),
    ]);
    setEmployees(emps.data);
    setSummary(sum.data.annuel);
  }, [companyId, year]);

  useEffect(() => { load().catch((e) => toast.error(apiError(e))); }, [load]);

  const remove = async (emp) => {
    if (!window.confirm(`Supprimer ${emp.nom} ${emp.prenom} ?`)) return;
    try {
      await api.delete(`/companies/${companyId}/employees/${emp.id}`);
      toast.success("Salarié supprimé");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!company) return <div className="text-muted-foreground">Créez d'abord une entreprise dans Paramètres.</div>;
  const taxById = Object.fromEntries((summary?.salaries || []).map((s) => [s.id, s]));

  return (
    <div className="space-y-6" data-testid="employees-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="eyebrow">{company.name} · exercice {year}</div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-foreground mt-1">Salariés</h1>
        </div>
        <div className="flex gap-2 flex-wrap justify-end">
          <Button variant="outline" onClick={() => setDupOpen(true)} disabled={!employees.length} data-testid="employee-duplicate-year-button"><CopyPlus className="h-4 w-4 mr-2" /> Dupliquer l'exercice</Button>
          <Button variant="outline" onClick={exportCsv} disabled={!employees.length} data-testid="employee-export-button"><Download className="h-4 w-4 mr-2" /> Exporter CSV</Button>
          <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="employee-import-button"><Upload className="h-4 w-4 mr-2" /> Importer CSV</Button>
          <Button onClick={() => setDialog({ open: true, employee: null })} data-testid="employee-add-button"><Plus className="h-4 w-4 mr-2" /> Nouveau salarié</Button>
        </div>
      </div>

      <div className="bg-card border border-border rounded-md overflow-hidden">
        <Table data-testid="employees-table">
          <TableHeader>
            <TableRow className="bg-muted/60">
              <TableHead>Salarié</TableHead>
              <TableHead>Poste</TableHead>
              <TableHead className="text-right">Brut annuel {year}</TableHead>
              <TableHead className="text-right">Base 4,25 %</TableHead>
              <TableHead className="text-right">Base 8,50 %</TableHead>
              <TableHead className="text-right">Base 13,60 %</TableHead>
              <TableHead className="text-right">Taxe brute</TableHead>
              <TableHead className="w-24" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {employees.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center py-12 text-muted-foreground" data-testid="employees-empty">
                <Users className="h-8 w-8 mx-auto text-muted-foreground/50 mb-2" />Aucun salarié. Ajoutez-en un ou importez un fichier CSV.
              </TableCell></TableRow>
            )}
            {employees.map((e) => {
              const t = taxById[e.id];
              return (
                <TableRow key={e.id} data-testid="employee-table-row" className="hover:bg-muted/50">
                  <TableCell><div className="font-medium text-foreground flex items-center gap-2">{e.nom} {e.prenom}{e.apprenti && <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border border-brand-200 bg-brand-50 text-brand-700 dark:bg-brand-500/15 dark:text-brand-300 dark:border-brand-500/40" data-testid="employee-apprenti-badge">Apprenti</span>}</div>{t?.exoneration > 0 && <div className="text-xs text-muted-foreground">Exonéré : {fmtEur(t.exoneration)}</div>}</TableCell>
                  <TableCell className="text-muted-foreground">{e.poste || "—"}</TableCell>
                  <TableCell className="text-right font-mono" data-testid="employee-annual-gross">{fmtEur(annualOf(e, year))}</TableCell>
                  <TableCell className="text-right font-mono text-brand-600 dark:text-brand-300">{fmtEur(t?.base1)}</TableCell>
                  <TableCell className="text-right font-mono text-amber-700 dark:text-amber-400">{fmtEur(t?.base2)}</TableCell>
                  <TableCell className="text-right font-mono text-red-700 dark:text-red-400">{fmtEur(t?.base3)}</TableCell>
                  <TableCell className="text-right font-mono font-semibold" data-testid="employee-tax">{fmtEur(t?.total)}</TableCell>
                  <TableCell>
                    <div className="flex justify-end gap-1">
                      <Button size="icon" variant="ghost" onClick={() => setDialog({ open: true, employee: e })} data-testid="employee-edit-button"><Pencil className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" className="text-red-600 dark:text-red-400 hover:text-red-700 dark:text-red-400" onClick={() => remove(e)} data-testid="employee-delete-button"><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {summary && employees.length > 0 && (
          <div className="flex justify-end gap-8 px-4 py-3 bg-muted/60 border-t border-border text-sm">
            <span className="text-muted-foreground">Masse salariale <span className="font-mono font-semibold text-foreground ml-2" data-testid="employees-total-payroll">{fmtEur(summary.masse_salariale)}</span></span>
            <span className="text-muted-foreground">Taxe brute <span className="font-mono font-semibold text-foreground ml-2" data-testid="employees-total-tax">{fmtEur(summary.taxe_brute)}</span></span>
          </div>
        )}
      </div>

      <EmployeeDialog open={dialog.open} employee={dialog.employee} companyId={companyId} year={year} onClose={() => setDialog({ open: false, employee: null })} onSaved={load} />
      <CsvImportDialog open={importOpen} companyId={companyId} year={year} onClose={() => setImportOpen(false)} onImported={load} />
      <DuplicateYearDialog open={dupOpen} companyId={companyId} sourceYear={year} onClose={() => setDupOpen(false)} onDone={(target) => setYear(target)} />
    </div>
  );
}
